import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Briefcase, MapPin, Clock, DollarSign, Building } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/components/AuthProvider';

interface JobPosting {
  id: string;
  businessId: string;
  title: string;
  description: string;
  requirements: string;
  payRate: string;
  jobType: 'full_time' | 'part_time' | 'contract' | 'internship' | 'volunteer';
  location: string;
  remote: boolean;
  contactEmail: string;
  contactPhone?: string;
  postedDate: string;
  active: boolean;
}

interface Business {
  id: string;
  name: string;
  category: string;
  city: string;
  state: string;
  foundationalBlackAmerican: boolean;
  businessType: 'established' | 'startup' | 'side_hustle' | 'youth_entrepreneur';
}

export default function JobsPage() {
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState<'all' | 'fba' | 'youth' | 'remote'>('all');
  const [selectedJobType, setSelectedJobType] = useState<string>('');

  const { data: jobs = [], isLoading } = useQuery<JobPosting[]>({
    queryKey: ['/api/jobs', { active: true }],
    queryFn: () => apiRequest('/api/jobs?active=true'),
  });

  const { data: businesses = [] } = useQuery<Business[]>({
    queryKey: ['/api/businesses'],
    queryFn: () => apiRequest('/api/businesses'),
  });

  // Create a map of business details
  const businessMap = businesses.reduce((acc, business) => {
    acc[business.id] = business;
    return acc;
  }, {} as Record<string, Business>);

  const filteredJobs = jobs.filter(job => {
    const business = businessMap[job.businessId];
    if (!business) return false;

    if (activeFilter === 'fba' && !business.foundationalBlackAmerican) return false;
    if (activeFilter === 'youth' && business.businessType !== 'youth_entrepreneur') return false;
    if (activeFilter === 'remote' && !job.remote) return false;
    if (selectedJobType && job.jobType !== selectedJobType) return false;

    return true;
  });

  const getJobTypeLabel = (type: string) => {
    switch (type) {
      case 'full_time': return 'Full Time';
      case 'part_time': return 'Part Time';
      case 'contract': return 'Contract';
      case 'internship': return 'Internship';
      case 'volunteer': return 'Volunteer';
      default: return type;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading job opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Job Opportunities
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover career opportunities with Foundational Black American businesses. 
            From established companies to young entrepreneurs, find your next role supporting Black excellence.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-wrap gap-4 mb-4">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                activeFilter === 'all' 
                  ? 'bg-red-700 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All Jobs ({jobs.length})
            </button>
            <button
              onClick={() => setActiveFilter('fba')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                activeFilter === 'fba' 
                  ? 'bg-red-700 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Black Professionals
            </button>
            <button
              onClick={() => setActiveFilter('youth')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                activeFilter === 'youth' 
                  ? 'bg-red-700 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Youth Entrepreneurs
            </button>
            <button
              onClick={() => setActiveFilter('remote')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                activeFilter === 'remote' 
                  ? 'bg-red-700 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Remote Jobs
            </button>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <select
              value={selectedJobType}
              onChange={(e) => setSelectedJobType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
            >
              <option value="">All Job Types</option>
              <option value="full_time">Full Time</option>
              <option value="part_time">Part Time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
              <option value="volunteer">Volunteer</option>
            </select>
          </div>
        </div>

        {/* Job Listings */}
        <div className="space-y-6">
          {filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
              <p className="text-gray-600">
                Try adjusting your filters or check back later for new opportunities.
              </p>
            </div>
          ) : (
            filteredJobs.map((job) => {
              const business = businessMap[job.businessId];
              return (
                <div key={job.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          job.jobType === 'full_time' ? 'bg-red-100 text-blue-900' :
                          job.jobType === 'part_time' ? 'bg-blue-100 text-blue-800' :
                          job.jobType === 'contract' ? 'bg-purple-100 text-purple-800' :
                          job.jobType === 'internship' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getJobTypeLabel(job.jobType)}
                        </span>
                        {job.remote && (
                          <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                            Remote
                          </span>
                        )}
                      </div>
                      
                      {business && (
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                          <div className="flex items-center gap-1">
                            <Building className="h-4 w-4" />
                            <Link href={`/business/${business.id}`} className="hover:text-red-700">
                              {business.name}
                            </Link>
                          </div>
                          {business.foundationalBlackAmerican && (
                            <span className="px-2 py-1 text-xs bg-red-100 text-blue-900 rounded-full">
                              FBA
                            </span>
                          )}
                          {business.businessType === 'youth_entrepreneur' && (
                            <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                              Youth
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {job.location}
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          {job.payRate}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Posted {formatDate(job.postedDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-gray-700 mb-2">{job.description}</p>
                    <div className="text-sm text-gray-600">
                      <strong>Requirements:</strong> {job.requirements}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <a
                      href={`mailto:${job.contactEmail}?subject=Application for ${job.title}`}
                      className="flex-1 bg-red-700 text-white px-4 py-2 rounded-md hover:bg-red-800 transition-colors text-center"
                    >
                      Apply Now
                    </a>
                    {job.contactPhone && (
                      <a
                        href={`tel:${job.contactPhone}`}
                        className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors text-center"
                      >
                        Call {job.contactPhone}
                      </a>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <div className="bg-red-50 rounded-lg p-8">
            <h3 className="text-xl font-bold text-green-900 mb-2">
              Business Owner?
            </h3>
            <p className="text-green-700 mb-4">
              Post your job openings and connect with talented candidates who want to support Black businesses.
            </p>
            <Link
              href="/add-business"
              className="inline-block bg-red-700 text-white px-6 py-3 rounded-md hover:bg-red-800 transition-colors"
            >
              List Your Business & Post Jobs
            </Link>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            Questions about job postings? Contact us at{' '}
            <a 
              href="mailto:info@blackbook.family" 
              className="text-red-700 hover:text-blue-900"
            >
              info@blackbook.family
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}